/* eslint-disable indent */
import { NonLinearProcessingLevel } from '@shure/shared/angular/data-access/system-api/core';
import {
	mapChannelGroupFromSysApi,
	mapNonLinearProcessingLevelFromSysApi
} from '@shure/shared/angular/data-access/system-api/mappers';

import { AcousticEchoCancellationChannel, AecReferenceChannel } from '../../api/models';
import { AecChannelFragment, AecReferenceSourceOptionFragment } from '../graphql/generated/aec.generated';
import { getAecReferenceChannelPriority } from '../utils';

export function mapAecChannelFromSysApi(channel: AecChannelFragment): AcousticEchoCancellationChannel {
	const {
		id,
		features: {
			name,
			acousticEchoCancellation,
			acousticEchoCancellationMonoReference,
			acousticEchoCancellationStereoReference,
			acousticEchoCancellationErleMeterSource
		},
		description: { constraints, features }
	} = channel;

	const monoReferenceOptions = acousticEchoCancellationMonoReference?.referenceSourceOptions
		? mapAecReferenceSourceOptions(acousticEchoCancellationMonoReference?.referenceSourceOptions)
		: [];

	const stereoReferenceOptions = acousticEchoCancellationStereoReference?.referenceSourceOptions
		? mapAecReferenceSourceOptions(acousticEchoCancellationStereoReference?.referenceSourceOptions)
		: [];

	return {
		id,
		name: name?.name,
		features: {
			acousticEchoCancellation: acousticEchoCancellation
				? {
						enabled: acousticEchoCancellation.enabled,
						nonLinearProcessingLevel:
							constraints.acousticEchoCancellation?.nonLinearProcessingLevel &&
							acousticEchoCancellation.nonLinearProcessingLevel
								? mapNonLinearProcessingLevelFromSysApi(
										acousticEchoCancellation.nonLinearProcessingLevel
								  )
								: undefined
				  }
				: {
						enabled: true,
						nonLinearProcessingLevel: mapNonLinearProcessingLevelFromSysApi(NonLinearProcessingLevel.Low)
				  },
			acousticEchoCancellationMonoReference: acousticEchoCancellationMonoReference
				? {
						reference: acousticEchoCancellationMonoReference.reference,
						selectedReferenceSource: acousticEchoCancellationMonoReference.selectedReferenceSource,
						referenceSourceOptions: monoReferenceOptions
				  }
				: undefined,
			acousticEchoCancellationStereoReference: acousticEchoCancellationStereoReference
				? {
						reference: acousticEchoCancellationStereoReference.reference,
						selectedLeftReferenceSource:
							acousticEchoCancellationStereoReference.selectedLeftReferenceSource,
						selectedRightReferenceSource:
							acousticEchoCancellationStereoReference.selectedRightReferenceSource,
						referenceSourceOptions: stereoReferenceOptions
				  }
				: undefined,
			acousticEchoCancellationErleMeterSource: features.acousticEchoCancellationErleMeterSource.supported
				? {
						channels:
							acousticEchoCancellationErleMeterSource?.audioChannels.map((channel) => {
								return {
									id: channel.id,
									index: channel.description.index,
									features: {
										lobeConfiguration: {
											enabled: channel.features.lobeConfiguration?.enabled ?? false
										},
										sendToMix: {
											enabled: channel.features.automixerInputSendToMonoMix?.enabled ?? false
										}
									}
								};
							}) ?? [],
						isMissing: !acousticEchoCancellationErleMeterSource
				  }
				: undefined,
			channelConfiguration: channel.description.features.channelConfiguration.supported
				? {
						enabled: channel.features.channelConfiguration?.enabled || false,
						isMissing: channel.features.channelConfiguration === null
				  }
				: undefined
		},
		constraints: {
			nonLinearProcessingLevel: {
				supportedModes:
					(constraints.acousticEchoCancellation?.nonLinearProcessingLevel?.options ?? []).map((mode) =>
						mapNonLinearProcessingLevelFromSysApi(mode)
					) || []
			},
			acousticEchoCancellationPeak: {
				level: {
					min: constraints.acousticEchoCancellationPeak?.level.range.min ?? -60,
					max: constraints.acousticEchoCancellationPeak?.level.range.max ?? 0,
					step: constraints.acousticEchoCancellationPeak?.level.range.step ?? 1
				}
			},
			acousticEchoCancellationErle: {
				level: {
					min: constraints.acousticEchoCancellationErle?.level.range.min ?? -5,
					max: constraints.acousticEchoCancellationErle?.level.range.max ?? 50,
					step: constraints.acousticEchoCancellationErle?.level.range.step ?? 1
				}
			},
			acousticEchoCancellationErleMeterSource: {
				levels:
					acousticEchoCancellationErleMeterSource?.audioChannels.map((channel) => {
						return {
							min: channel.description.constraints.acousticEchoCancellationErle?.level.range.min ?? -5,
							max: channel.description.constraints.acousticEchoCancellationErle?.level.range.max ?? 50,
							step: channel.description.constraints.acousticEchoCancellationErle?.level.range.step ?? 1
						};
					}) ?? []
			},
			acousticEchoCancellationMonoReference: {
				referenceSourceOptions: {
					supported:
						constraints.acousticEchoCancellationMonoReference?.referenceSourceOptions.supported ?? false
				}
			},
			acousticEchoCancellationStereoReference: {
				referenceSourceOptions: {
					supported:
						constraints.acousticEchoCancellationMonoReference?.referenceSourceOptions.supported ?? false
				}
			}
		}
	};
}

export function mapAecReferenceSourceOptions(
	referenceSourceOptions: AecReferenceSourceOptionFragment[]
): AecReferenceChannel[] {
	return referenceSourceOptions
		.map((option) => ({
			...option,
			description: {
				...option.description,
				group: mapChannelGroupFromSysApi(option.description.group)
			}
		}))
		.sort((a, b) =>
			getAecReferenceChannelPriority(a.description?.group) >= getAecReferenceChannelPriority(b.description?.group)
				? 1
				: -1
		)
		.map((option) => ({
			id: option.id,
			features: {
				name: option.features.name?.name ? option.features.name.name : ''
			}
		}));
}
